import type { ReactNode } from 'react';
import { Badge } from '@xiltrion/ui/badge';
import { Card as CardUi, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { EstadoCargando, EstadoError, EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { Field as FieldUi, FieldDescription, FieldError, FieldLabel } from '@xiltrion/ui/field';
import { cn } from '@xiltrion/ui/utils';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, REGEXP_ONLY_DIGITS } from '@xiltrion/ui/input-otp';
import { CERTIFIED_STATE_LABEL } from '@pqrs/contracts';
import type { Due } from '@/api/types';

/** Ícono de los widgets de negocio: un nombre de Iconify de la colección mdi (`mdi:inbox-outline`). */
export type IconoUi = NombreIcono;

/** Semantic colours — never colour alone: every tag carries text and an icon. */
export const STATUS_LABEL: Record<string, string> = {
  RECEIVED: 'Recibida', PROCESSING: 'En triaje', PENDING_ASSIGNMENT: 'Por asignar', IN_PROGRESS: 'En trámite', WAITING_INFORMATION: 'Esperando información',
  WAITING_APPROVAL: 'En aprobación', WAITING_SIGNATURE: 'En firma', TRANSFERRED: 'Trasladada', ESCALATED: 'Escalada', RESOLVED: 'Resuelta', CLOSED: 'Cerrada', ARCHIVED: 'Archivada',
  PENDING: 'Pendiente', BLOCKED: 'Bloqueada', COMPLETED: 'Completada', CANCELLED: 'Cancelada',
};
type Severity = 'info' | 'success' | 'warning' | 'danger' | 'secondary';
const STATUS_SEVERITY: Record<string, Severity> = {
  RECEIVED: 'info', PROCESSING: 'info', PENDING_ASSIGNMENT: 'warning', IN_PROGRESS: 'info', WAITING_INFORMATION: 'warning', WAITING_APPROVAL: 'info', WAITING_SIGNATURE: 'info',
  TRANSFERRED: 'secondary', ESCALATED: 'danger', RESOLVED: 'success', CLOSED: 'success', ARCHIVED: 'secondary', PENDING: 'info', BLOCKED: 'danger', COMPLETED: 'success', CANCELLED: 'secondary',
};
/** Severidad PrimeReact → Badge de @xiltrion/ui (README §11): solo tokens. */
const SEVERITY_BADGE: Record<Severity, { variant?: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }> = {
  info: { className: 'bg-primary/15 text-primary' },
  success: { className: 'bg-success/15 text-success' },
  warning: { className: 'bg-warning/20 text-foreground' },
  danger: { variant: 'destructive' },
  secondary: { variant: 'secondary' },
};
/** Tamaños del `Badge` del paquete; en una misma fila (encabezado del expediente) todas las etiquetas llevan el mismo. */
export type BadgeSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';
/** `children` va después de la palabra, dentro de la etiqueta (p. ej. el aviso de «Requiere revisión» en triaje). */
export function StatusTag({ status, size, children }: { status: string; size?: BadgeSize; children?: ReactNode }) {
  const sev = SEVERITY_BADGE[STATUS_SEVERITY[status] ?? 'secondary'];
  return <Badge variant={sev.variant} size={size} className={sev.className}>{STATUS_LABEL[status] ?? status}{children}</Badge>;
}

const RASTREO_SEVERITY: Record<string, Severity> = { entregado: 'success', abierto: 'success', leido: 'success', no_entregado: 'danger', error: 'danger', cancelado: 'danger' };
/** Rastreo de un correo certificado: el estado menos avanzado (o el problema) y cuántos llegaron. */
export function RastreoTag({ rastreo }: { rastreo: { estado: string; total: number; entregados: number } | null | undefined }) {
  if (!rastreo) return <span className="text-muted-foreground">—</span>;
  const sev = SEVERITY_BADGE[RASTREO_SEVERITY[rastreo.estado] ?? 'secondary'];
  const label = CERTIFIED_STATE_LABEL[rastreo.estado] ?? rastreo.estado;
  return <Badge variant={sev.variant} className={sev.className} title={`${rastreo.entregados} de ${rastreo.total} entregado(s)`}>{rastreo.total > 1 ? `${label} · ${rastreo.entregados}/${rastreo.total}` : label}</Badge>;
}

export const PRIORITY_LABEL: Record<string, string> = { LOW: 'Baja', NORMAL: 'Normal', HIGH: 'Alta', CRITICAL: 'Crítica' };
export function PriorityTag({ priority }: { priority: string }) {
  const sev = SEVERITY_BADGE[priority === 'CRITICAL' ? 'danger' : priority === 'HIGH' ? 'warning' : priority === 'LOW' ? 'secondary' : 'info'];
  return (
    <Badge variant={sev.variant} className={`rounded-md ${sev.className ?? ''}`}>
      {priority === 'CRITICAL' && <Icono nombre='mdi:alert-outline' aria-hidden />}
      {PRIORITY_LABEL[priority] ?? priority}
    </Badge>
  );
}
/** Engine names for screens that cannot list /decisions/engines; mirrors the API's default labels. */
export const engineLabel = (name: string | null | undefined) => (name ? ({ rules: 'Texto', xiltrion: 'Xiltrion Decision Engine' } as Record<string, string>)[name] ?? name.toUpperCase() : '—');
export const CHANNEL_LABEL: Record<string, string> = { WEB_PORTAL: 'Portal', EMAIL: 'Email', PHYSICAL: 'Físico', API: 'API', INTERNAL: 'Interno' };

/** "Vence en 18h 32m" / "Vence en 3h" / "Vencida hace 2h", con ícono según el tono. */
/** `compacto`: solo el plazo («21d 2h»; vencida: «hace 2d 3h»), para una columna que ya se titula Vencimiento. */
/**
 * El vencimiento tal como lo manda el API (días hábiles: «Vence en 3 días hábiles», «Vence hoy a las 5:00 P.M.», «Vencida hace
 * 2 días hábiles»). `compacto`: sin el verbo, para una celda. `destacado`: píldora con fondo, para la cabecera del expediente.
 */
/** Cuánto falta del término. `destacado` lo pinta como `Badge` del paquete (del tamaño `size`, el mismo que las etiquetas vecinas); sin él, solo texto con ícono. */
export function DueBadge({ due, dueAt, compacto = false, destacado = false, size }: { due: Due | null | undefined; dueAt?: string | null; compacto?: boolean; destacado?: boolean; size?: BadgeSize }) {
  if (!due) return <span className="text-muted-foreground">—</span>;
  const texto = compacto ? due.text.replace(/^Vence en /, '').replace(/^Vence /, '').replace(/^Vencida hace /, 'hace ').replace(/^Vencida el /, 'venció el ').replace(/^Venció /, 'venció ') : due.text;
  const icono: NombreIcono = due.tone === 'overdue' ? 'mdi:alert-circle-outline' : due.tone === 'warning' ? 'mdi:alert-outline' : 'mdi:clock-outline';
  const title = dueAt ? new Date(dueAt).toLocaleString('es-CO') : undefined;
  if (destacado) {
    const variant = due.tone === 'overdue' ? 'destructive' : due.tone === 'warning' ? 'warning' : 'secondary';
    return <Badge variant={variant} size={size} className={due.tone === 'ok' ? undefined : 'font-semibold'} title={title}><Icono nombre={icono} aria-hidden /> {texto}</Badge>;
  }
  const cls = due.tone === 'overdue' ? 'text-destructive font-semibold' : due.tone === 'warning' ? 'text-warning font-medium' : 'text-foreground';
  return (
    <span className={`inline-flex items-center gap-1 ${cls}`} title={title}>
      <Icono nombre={icono} className="size-3.5 shrink-0" aria-hidden /> {texto}
    </span>
  );
}

export function ConfidenceBadge({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  const pct = Math.round(value * 100);
  const tone = pct >= 90 ? 'text-success' : pct >= 70 ? 'text-warning' : 'text-destructive';
  return <span className={`${tone} font-medium tnum`} aria-label={`Confianza ${pct} por ciento`}>{pct}%</span>;
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return <EncabezadoPagina titulo={title} descripcion={subtitle} acciones={actions} className="mb-4" />;
}

/** Loading / empty / error states, sobre los estados de @xiltrion/ui. */
export function LoadingState({ rows = 6 }: { rows?: number }) {
  return <EstadoCargando filas={rows} />;
}

export function EmptyState({ title, hint, icon = 'mdi:inbox-outline', action }: { title: string; hint?: string; icon?: IconoUi; action?: ReactNode }) {
  return <EstadoVacio titulo={title} descripcion={hint} icono={icon} accion={action} />;
}

export function ErrorState({ error, onRetry, title = 'No pudimos cargar esta información.' }: { error?: unknown; onRetry?: () => void; title?: string }) {
  return <EstadoError titulo={title} error={error} onReintentar={onRetry} />;
}

/** Titled section without a card: for content that already lives inside a card (the next-action card of the expediente). */
/**
 * Campo de formulario del backoffice: etiqueta, control, ayuda y error con una sola separación en toda la
 * app (sobre `Field` de @xiltrion/ui). La ayuda y el error llevan los ids `${htmlFor}-hint` y
 * `${htmlFor}-error` para enlazarlos con `aria-describedby` cuando haga falta. No armar etiqueta y control
 * a mano con `Label` y un `div`.
 */
export function Field({ label, htmlFor, hint, error, children, required, className }: {
  label: ReactNode; htmlFor?: string; hint?: ReactNode; error?: ReactNode; children: ReactNode; required?: boolean; className?: string;
}) {
  return (
    <FieldUi data-invalid={error ? true : undefined} className={cn('gap-1.5', className)}>
      <FieldLabel htmlFor={htmlFor} className="gap-0.5">{label}{required && <span className="text-destructive" aria-hidden>*</span>}</FieldLabel>
      {children}
      {hint && <FieldDescription id={htmlFor ? `${htmlFor}-hint` : undefined} className="text-xs">{hint}</FieldDescription>}
      {error && <FieldError id={htmlFor ? `${htmlFor}-error` : undefined} className="text-xs">{error}</FieldError>}
    </FieldUi>
  );
}

export function Section({ title, children, className = '', actions }: { title?: ReactNode; children: ReactNode; className?: string; actions?: ReactNode }) {
  return (
    <section className={`flex flex-col gap-2 ${className}`}>
      {(title || actions) && <div className="flex items-center justify-between gap-2">{title && <h3 className="text-sm font-semibold m-0">{title}</h3>}{actions}</div>}
      <div>{children}</div>
    </section>
  );
}

/** Tarjeta de negocio. `description` es la línea que explica la tarjeta: va en el encabezado, con `CardDescription` del paquete (mismo tamaño que el resto del texto), nunca más pequeña. */
export function Card({ title, description, children, className = '', actions }: { title?: ReactNode; description?: ReactNode; children: ReactNode; className?: string; actions?: ReactNode }) {
  return (
    <CardUi className={`gap-3 rounded-lg py-4 ${className}`}>
      {(title || description || actions) && (
        <CardHeader className={description ? 'px-4 gap-y-1' : 'px-4 gap-y-0'}>
          {title && <CardTitle className="text-base">{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
          {actions && <CardAction>{actions}</CardAction>}
        </CardHeader>
      )}
      <CardContent className="px-4">{children}</CardContent>
    </CardUi>
  );
}

export function KeyValue({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-6 gap-y-2 m-0">
      {items.map((it) => (
        <div key={it.label} className="contents">
          <dt className="text-muted-foreground">{it.label}</dt>
          <dd className="m-0 min-w-0 break-words">{it.value ?? <span className="text-muted-foreground">—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

export const fmtDate = (d: string | Date | null | undefined, withTime = true) => (d ? new Date(d).toLocaleString('es-CO', withTime ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' }) : '—');

/**
 * Código de un solo uso de 6 dígitos (app autenticadora o código enviado al correo): una casilla por
 * dígito, en dos grupos de tres, con el tamaño por defecto de shadcn (el mismo de ReUI); `lg` para el diálogo de firma.
 * Solo acepta dígitos; `onChange` recibe lo escrito.
 */
export function CodeInput({ id, value, onChange, autoFocus, disabled, size = 'default' }: {
  id: string; value: string; onChange: (v: string) => void; autoFocus?: boolean; disabled?: boolean; size?: 'default' | 'lg';
}) {
  const slot = size === 'lg' ? 'h-11 w-11 text-xl' : undefined;
  return (
    <InputOTP id={id} maxLength={6} pattern={REGEXP_ONLY_DIGITS} inputMode="numeric" autoComplete="one-time-code" value={value} onChange={onChange}
      autoFocus={autoFocus} disabled={disabled} containerClassName="tnum">
      <InputOTPGroup>{[0, 1, 2].map((i) => <InputOTPSlot key={i} index={i} className={slot} />)}</InputOTPGroup>
      <InputOTPSeparator />
      <InputOTPGroup>{[3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className={slot} />)}</InputOTPGroup>
    </InputOTP>
  );
}
